import { Injectable, inject, computed, Signal } from '@angular/core';
import { GlobalTimezoneService } from './global-timezone.service';
import { ITimeZone } from '../api/mylb/interfaces/itime-zone.interface';

export interface LessonTimeDisplayOptions {
	showOriginalTimezone?: boolean;
	showTimezoneContext?: boolean;
	format?: 'short' | 'medium' | 'long' | 'full';
	timeOnly?: boolean;
}

export interface LessonTimeConversion {
	/** The UTC instant from the datetimeoffset value */
	originalTime: Date;
	/** Same as originalTime - datetimeoffset doesn't need conversion, only formatting */
	displayTime: Date;
	/** @deprecated Not applicable for datetimeoffset - always null */
	originalTimezone: ITimeZone | null;
	/** The timezone to use when formatting the time for display */
	displayTimezone: ITimeZone | null;
	/** @deprecated Use lesson metadata for timezone context instead */
	contextMessage?: string;
	/** @deprecated Not applicable for datetimeoffset - always false */
	timezonesDiffer: boolean;
}

/**
 * Service for handling lesson time conversions and display
 * Lessons use datetimeoffset which makes timezone conversion easier than availability blocks
 */
@Injectable({
	providedIn: 'root'
})
export class LessonTimezoneService {
	private _globalTimezoneService = inject(GlobalTimezoneService);

	// Current timezone context from global service
	public selectedTimezone = this._globalTimezoneService.selectedTimezone;
	public browserTimezone = this._globalTimezoneService.browserTimezone;
	public timezoneContext = this._globalTimezoneService.timezoneContext;

	/**
	 * Converts a lesson datetime (stored as datetimeoffset) to the user's selected timezone
	 *
	 * Since lessons use datetimeoffset, they already contain the absolute UTC instant.
	 * This method simply returns the Date object which can then be formatted in the user's
	 * selected timezone using Intl.DateTimeFormat or Angular's date pipe.
	 *
	 * Note: The originalTimezoneId parameter is deprecated and ignored. Datetimeoffset values
	 * are timezone-independent - they represent a specific moment in time regardless of timezone.
	 */
	public convertLessonTime(lessonDateTime: string | Date, originalTimezoneId?: number, availableTimezones?: ITimeZone[]): LessonTimeConversion {
		const utcTime = typeof lessonDateTime === 'string' ? new Date(lessonDateTime) : lessonDateTime;
		const selectedTz = this.selectedTimezone();

		// For datetimeoffset, no conversion is needed - the Date object already represents
		// the correct UTC instant. The timezone is only used for display formatting.
		return {
			originalTime: utcTime,
			displayTime: utcTime,
			originalTimezone: null, // Not applicable for datetimeoffset
			displayTimezone: selectedTz,
			contextMessage: '', // Removed - timezone context should come from lesson metadata, not the datetime itself
			timezonesDiffer: false // Not applicable - datetimeoffset is timezone-independent
		};
	}

	/**
	 * Converts multiple lesson times efficiently
	 * Note: timezoneId parameter is deprecated - datetimeoffset values are timezone-independent
	 */
	public convertLessonTimes(
		lessonDateTimes: Array<{ dateTime: string | Date; timezoneId?: number }>,
		availableTimezones?: ITimeZone[]
	): LessonTimeConversion[] {
		return lessonDateTimes.map((lesson) => this.convertLessonTime(lesson.dateTime));
	}

	/**
	 * Formats a lesson time for display with timezone context
	 */
	public formatLessonTime(conversion: LessonTimeConversion, options: LessonTimeDisplayOptions = {}): string {
		const { format = 'short', timeOnly = false, showOriginalTimezone = false, showTimezoneContext = false } = options;

		const selectedTz = this.selectedTimezone();
		if (!selectedTz) {
			return conversion.originalTime.toLocaleString();
		}

		// Use Intl.DateTimeFormat for proper timezone-aware formatting
		const formatOptions: Intl.DateTimeFormatOptions = timeOnly
			? { timeStyle: format as any, timeZone: selectedTz.timeZoneName }
			: { dateStyle: format as any, timeStyle: format as any, timeZone: selectedTz.timeZoneName };

		let formatted = conversion.displayTime.toLocaleString('en-US', formatOptions);

		// Add timezone context if requested
		if (showTimezoneContext && selectedTz.abbreviation) {
			formatted += ` ${selectedTz.abbreviation}`;
		}

		// Add original timezone info if different and requested
		if (showOriginalTimezone && conversion.timezonesDiffer && conversion.originalTimezone) {
			formatted += ` (originally ${conversion.originalTimezone.abbreviation})`;
		}

		return formatted;
	}

	/**
	 * Gets timezone context for lesson scheduling
	 * Useful for showing warnings when scheduling across timezones
	 */
	public getLessonSchedulingContext(): {
		message: string;
		type: 'info' | 'warning' | 'none';
		showTimezoneSelector: boolean;
	} {
		const context = this.timezoneContext();

		return {
			message: context.contextMessage,
			type: context.contextType,
			showTimezoneSelector: context.showTimezoneSelector
		};
	}

	/**
	 * Utility to check if a lesson time conflicts with user's preferred schedule
	 * This could be expanded to include business logic about scheduling preferences
	 */
	public checkLessonTimeConflicts(
		lessonDateTime: string | Date,
		userPreferredStartHour: number = 8,
		userPreferredEndHour: number = 22
	): { hasConflict: boolean; reason?: string } {
		const conversion = this.convertLessonTime(lessonDateTime);
		const selectedTz = this.selectedTimezone();

		if (!selectedTz) {
			return { hasConflict: false };
		}

		// Get the hour in user's timezone
		const userHour = conversion.displayTime.getHours();

		if (userHour < userPreferredStartHour) {
			return {
				hasConflict: true,
				reason: `Lesson is scheduled at ${userHour}:00, which is before your preferred start time of ${userPreferredStartHour}:00`
			};
		}

		if (userHour > userPreferredEndHour) {
			return {
				hasConflict: true,
				reason: `Lesson is scheduled at ${userHour}:00, which is after your preferred end time of ${userPreferredEndHour}:00`
			};
		}

		return { hasConflict: false };
	}
}
